// Collab-konvolutten: sanntids-samhandling (digital pointer, delte tegninger,
// stridsmeldinger, RFI, ORBAT-hendelser m.fl.) mellom hubben (`@skade/hub`) og
// COP-klienten (`@skade/cop`). ÉN kilde til sannhet — se QA-003.
//
// FØR fantes UNIONEN som tre hånddupliserte kopier (hub `CollabIn`/`CollabOut`,
// COP sitt `CollabMessage` + `Feed.send`-signaturen, OG `LiveFeed.send`s EGEN,
// enda snevrere signatur), og de hadde divergert reelt:
//  1. `LiveFeed.send()` sin egen parametertype manglet `'teig' | 'teig-slett'
//     | 'rfi-svar' | 'kalender-slett'` — nye kinds lagt til i `Feed.send`
//     (grensesnittet) ble aldri lagt til i selve implementasjonens signatur.
//     Virket i praksis (JS bryr seg ikke), men typen løy om hva som faktisk
//     ble sendt.
//  2. VERRE: `LiveFeed`s WS-`onmessage` (mottak, ikke sending) bygde
//     `CollabMessage`-objektet til lytterne FELT FOR FELT, og glemte å ta med
//     `teig`, `teigId`, `rfiSvar`, `kalenderId` OG `ts` — feltene
//     `main.ts` sin `onCollab`-handler faktisk leser (`msg.teig`, `msg.teigId`,
//     `msg.rfiSvar`, `msg.kalenderId`). Resultat: en teig/kalender-sletting/
//     RFI-svar sendt fra hubben NÅDDE klienten over WS, men ble kastet bort i
//     feed-laget FØR den nådde handleren — «ukjente events dør stille», bare
//     på klientsiden i stedet for på hubben. Rettet her ved at BEGGE sider nå
//     deler samme `CollabOut`/`CollabMessage`-type, som gjør et manglende felt
//     til en TYPEFEIL i stedet for en stille NaN/undefined i produksjon.
import { z } from 'zod';
/** `type`-feltet klienten kan sende INN til hubben. ÉN kilde — bygg
 * `CollabIn`-unionen og `Feed.send`s parametertype fra denne, ikke fra
 * separate hånd-lister som kan divergere igjen. */
export const COLLAB_IN_TYPES = [
    'pointer', 'draw', 'units', 'report', 'intel', 'field', 'chat', 'rfi',
    'casualty', 'logstat', 'resupply', 'checklist', 'eso', 'orbat', 'calendar',
    'myndighet', 'teig', 'teig-slett', 'rfi-svar', 'kalender-slett', 'emergency',
];
/** `kind`-feltet hubben kringkaster UT. Supersett av `CollabInType`: `gnadm`
 * kommer bare fra GNÅ-broen (1:1-svar), aldri direkte fra COP-klienten. */
export const COLLAB_OUT_KINDS = [...COLLAB_IN_TYPES, 'gnadm'];
// ── NACK — «ukjente events dør stille» (QA-003, RÅ-2) ───────────────────────
//
// Hubben validerer hvert innkommende collab-event mot sitt skjema
// (`shapeCollab`). Før returnerte den bare `null` og droppet meldingen —
// avsenderen fikk ALDRI vite at noe gikk tapt. Et felt uten samband som
// tror en ordre er sendt, men der hubben aldri forsto den, er nøyaktig den
// stille svikten §6/§4 i grunnloven advarer mot. NACK-en løser det MINIMALT:
// én melding tilbake til AVSENDEREN (aldri kringkastet — det ville vært en
// ny fanout-amplifiseringsvei for søppel), ikke en redelivery-mekanisme.
export const CollabNackSchema = z.object({
    type: z.literal('nack'),
    kind: z.literal('nack'),
    /** Menneskelesbar begrunnelse (norsk), til logg/toast — ikke til parsing. */
    reason: z.string().max(500),
    /** Den rå meldingen hubben ikke forsto, ekko for feilsøking hos avsender. */
    mottatt: z.unknown(),
    ts: z.number(),
});
/** Bygg en NACK-konvolutt (hub-side). */
export function buildCollabNack(reason, mottatt, now) {
    return { type: 'nack', kind: 'nack', reason, mottatt, ts: now };
}
/** Parse en NACK-konvolutt (klient-side, defensivt — feltdata er upålitelig). */
export function parseCollabNack(raw) {
    const r = CollabNackSchema.safeParse(raw);
    return r.success ? r.data : null;
}
//# sourceMappingURL=collab.js.map